import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { guessUserTimezone } from 'utils/helpers/dateTime';
import { type PlanDataType } from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import { type DispatchPlanHistoryResponseItemType } from '../types/dispatch-summary.types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Calculates the UTC start and end timestamps for the given planData object.
 * Adds configurable buffer minutes before and after the earliest and latest timestamps.
 *
 */
export const getPlanTimeRange = (
  masterAlgoDataByBumperNo: Record<string, PlanDataType[]>,
  approvedPlanData: PlanDataType[],
  timezone: string,
  isEndOfDay: boolean,
  { startBufferMinutes = 10, endBufferMinutes = 10 } = {}
) => {
  const resolvedTz = timezone ?? guessUserTimezone();
  const todayInTz = dayjs().tz(resolvedTz);
  const defaultRange = {
    startDateWithTime: todayInTz.startOf('day').utc().valueOf(),
    endDateWithTime: isEndOfDay
      ? todayInTz.add(1, 'day').startOf('day').utc().valueOf()
      : todayInTz.utc().valueOf(),
  };

  const extractValidTimestamps = (plans: PlanDataType[]) => {
    return plans
      .flatMap((plan) => [
        dayjs.utc(plan.start_ts).valueOf(),
        dayjs.utc(plan.end_ts).valueOf(),
      ])
      .filter((timestamp) => !isNaN(timestamp))
      .sort((a, b) => a - b);
  };

  // master algo data
  let masterTimestamps: number[] = [];
  if (
    masterAlgoDataByBumperNo &&
    typeof masterAlgoDataByBumperNo === 'object'
  ) {
    const flattenedScheduleArr = Object.values(masterAlgoDataByBumperNo).flat();
    masterTimestamps = extractValidTimestamps(flattenedScheduleArr);
  }

  // approved plan data
  let approvedTimestamps: number[] = [];
  if (Array.isArray(approvedPlanData) && approvedPlanData.length > 0) {
    approvedTimestamps = extractValidTimestamps(approvedPlanData);
  }

  // if both empty return defaults
  if (masterTimestamps.length === 0 && approvedTimestamps.length === 0) {
    return {
      startDateWithTime: defaultRange.startDateWithTime,
      endDateWithTime: defaultRange.endDateWithTime,
      masterRange: defaultRange,
      approvedRange: defaultRange,
    };
  }

  // helper to get min/max range
  const getRange = (timestamps: number[]) => {
    if (timestamps.length === 0) return defaultRange;
    const earliest = Math.min(...timestamps);
    const latest = Math.max(...timestamps);
    return {
      startDateWithTime: dayjs.utc(earliest).valueOf(),
      endDateWithTime: dayjs.utc(latest).valueOf(),
    };
  };

  // individual ranges

  const masterRange = getRange(masterTimestamps);
  const approvedRange = getRange(approvedTimestamps);

  // overall range (combined)
  const allTimestamps = [...masterTimestamps, ...approvedTimestamps];
  const earliest = Math.min(...allTimestamps);
  const latest = Math.max(...allTimestamps);

  return {
    startDateWithTime: dayjs
      .utc(earliest)
      .subtract(startBufferMinutes, 'minute')
      .valueOf(),
    endDateWithTime: dayjs
      .utc(latest)
      .add(endBufferMinutes, 'minute')
      .valueOf(),
    masterRange,
    approvedRange,
  };
};

/**
 * Finds the active dispatch plan for the current day.
 * A plan is considered active if its startDateWithTime falls within today (UTC-based).
 * If multiple plans exist, returns the one with the latest start time.
 */
export const getActivePlanForTodayFromPlanHistory = (
  plans: DispatchPlanHistoryResponseItemType[] = [],
  todayInTz: dayjs.Dayjs
): DispatchPlanHistoryResponseItemType | null => {
  try {
    if (!Array.isArray(plans)) {
      console.error('Expected "plans" to be an array.');
      return null;
    }

    if (!todayInTz || !dayjs.isDayjs(todayInTz)) {
      console.error('Invalid "todayInTz" provided. Expected a dayjs object.');
      return null;
    }

    const startOfTodayUtc = todayInTz.startOf('day').utc();
    const endOfTodayUtc = todayInTz.endOf('day').utc();

    const todaysPlans = plans.filter((plan) => {
      if (!plan?.startDateWithTime) return false;

      const planStartUtc = dayjs.utc(plan.startDateWithTime);
      if (!planStartUtc.isValid()) {
        console.warn(`Invalid date in plan: ${plan.startDateWithTime}`);
        return false;
      }

      return (
        planStartUtc.isSameOrAfter(startOfTodayUtc) &&
        planStartUtc.isBefore(endOfTodayUtc)
      );
    });

    if (todaysPlans.length === 0) return null;

    const latestActivePlan = todaysPlans.reduce((latest, current) =>
      dayjs
        .utc(current.startDateWithTime)
        .isAfter(dayjs.utc(latest.startDateWithTime))
        ? current
        : latest
    );

    return latestActivePlan;
  } catch (error) {
    console.error('Error while determining active plan:', error);
    return null;
  }
};
